import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  AlineTransforms,
  CANNED_FIELD_METADATA,
  CollaborationEditor,
  declineParamKey,
  defaultWorkflowFileSort,
  EditingMode,
  isCannedFieldElement,
  navToNextFieldParamKey,
  PDFCustomDataKey,
  Queries,
  SelEventSource,
  walkThroughParamKey,
} from '@aline/common';
import type { UUID } from '@aline/common';
import { link } from '@aline/routes';
import { makeVar } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useReactiveVar } from '@apollo/client/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';
import { Editor } from 'slate';
import type { ExistingFieldRecipient } from 'src/components';
import {
  getVariablePanelFocusedField,
  setVariablePanelFocusedField,
  useAreAnnotationsLoaded,
  useScrollFieldIntoView,
  useVariablePanelFocusedField,
} from 'src/components';
import { pendingCannedFieldValueIdsByWorkflowIdVar } from 'src/components/variables/document-variables/canned-field-write-state';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { useRequiredEditorRuntime } from 'src/editor/editor-runtime-registry';
import {
  useEditorTabStateActions,
  useEditorTabStateSelectorForTabId,
} from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import {
  updateUrlWithoutRefresh,
  useDocumentParams,
  useGetCompareFieldsByDocPosition,
} from 'src/helpers';
import { IS_MOBILE } from 'src/helpers/environment';
import {
  getIsFieldComplete,
  useFieldsChanged,
  useGetCurrentDoc,
  useGetDocumentFields,
  useGetDocumentWorkflow,
  useGetEditorUserState,
  useNotificationDispatch,
  useWorkflowCannedFields,
} from 'src/hooks';
import { useEditor, useEditorLoading, useMaybeEditor } from 'src/layout-engine';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { useCurrentTabIdOrThrow } from 'src/layouts/tabs/tab-active-context';
import { NotificationsActionType } from 'src/notifications';
import type { AnswerOptionSubset } from 'src/types';
import { getWorkflowWasSentForSig } from 'src/workflow';
import { v4 as uuid4 } from 'uuid';

import {
  setFinishSigningUnloadGuardForWorkflow,
  useFinishSigningUnloadGuard,
} from './finish-signing-unload-guard';
import { getWalkthroughAdvance } from './walkthrough-advance';
import type { RequesterFieldCounts } from './walkthrough-advance';

interface WalkthroughState {
  requesterWalkthroughInfo: RequesterWalkthroughInfo;
}

const defaultWalkthroughState: WalkthroughState = {
  requesterWalkthroughInfo: {
    hasPendingDocumentSave: false,
    orderedWalkthroughDocuments: [],
    ready: false,
    requesterActionsToDo: [],
    walkthroughDocumentsById: {},
  },
};

export const walkthroughStateVar = makeVar<WalkthroughState>(defaultWalkthroughState);

export function setWalkthroughState(
  stateOrFunc: Partial<WalkthroughState> | ((state: WalkthroughState) => Partial<WalkthroughState>)
): void {
  const currentState = walkthroughStateVar();
  const newState = typeof stateOrFunc === 'function' ? stateOrFunc(currentState) : stateOrFunc;

  walkthroughStateVar({ ...currentState, ...newState });
}

export type { RequesterFieldCounts } from './walkthrough-advance';

interface WalkthroughHookState {
  advanceWalkthrough: (
    event: React.SyntheticEvent,
    options?: { canExecuteFinish?: boolean }
  ) => Promise<void>;
  fieldActionsPendingSave: boolean;
  isFieldWalkthroughFinished: boolean;
  isRequesterAlreadyFinished: boolean;
  requesterFieldCounts: RequesterFieldCounts;
  requesterNeedsToClickFinish: boolean;
  submitting: boolean;
  walkThroughActive: boolean;
}

export function useWalkthroughActive() {
  const location = useLocation();
  const walkThroughActive = new URLSearchParams(location.search).has(walkThroughParamKey);
  return { walkThroughActive };
}

export function useExitWalkthrough() {
  const navigate = useNavigate();
  const location = useLocation();
  const exitWalkthrough = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete(walkThroughParamKey);
    params.delete(declineParamKey);
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true }
    );
  }, [location, navigate]);

  return { exitWalkthrough };
}

interface WalkthroughCtaOptions {
  // Defaults to 'Finish Signing' (desktop wording). Mobile passes 'Finish'.
  finishLabel?: 'Finish' | 'Finish Signing';
  /**
   * When the user is in the "ready to finish" state, run this instead of
   * calling advanceWalkthrough directly. Mobile uses this to open a
   * confirmation sheet first; desktop omits it and finalizes on a single tap.
   */
  onConfirmFinishNeeded?: () => void;
}

interface WalkthroughCtaState {
  buttonText: 'Done' | 'Finish' | 'Finish Signing' | 'Next';
  disabled: boolean;
  handleClick: (event: React.SyntheticEvent) => void;
  /**
   * Re-exported from useWalkthrough so consumers that also need these flags
   * don't have to call useWalkthrough() a second time (it has a heavy
   * transitive hook tree).
   */
  isRequesterAlreadyFinished: boolean;
  requesterNeedsToClickFinish: boolean;
  submitting: boolean;
}

export function getIsWalkthroughCtaDisabled(args: {
  fieldActionsPendingSave: boolean;
  requesterNeedsToClickFinish: boolean;
}): boolean {
  return args.requesterNeedsToClickFinish && args.fieldActionsPendingSave;
}

export function useWalkthroughCta(options: WalkthroughCtaOptions = {}): WalkthroughCtaState {
  const { finishLabel = 'Finish Signing', onConfirmFinishNeeded } = options;
  const {
    advanceWalkthrough,
    fieldActionsPendingSave,
    isRequesterAlreadyFinished,
    requesterNeedsToClickFinish,
    submitting,
  } = useWalkthrough();

  const buttonText = isRequesterAlreadyFinished
    ? 'Done'
    : requesterNeedsToClickFinish
      ? finishLabel
      : 'Next';

  const handleClick = useCallback(
    (event: React.SyntheticEvent) => {
      if (requesterNeedsToClickFinish && onConfirmFinishNeeded) {
        onConfirmFinishNeeded();
        return;
      }
      void advanceWalkthrough(event, { canExecuteFinish: true });
    },
    [advanceWalkthrough, onConfirmFinishNeeded, requesterNeedsToClickFinish]
  );

  return {
    buttonText,
    disabled: getIsWalkthroughCtaDisabled({
      fieldActionsPendingSave,
      requesterNeedsToClickFinish,
    }),
    handleClick,
    isRequesterAlreadyFinished,
    requesterNeedsToClickFinish,
    submitting,
  };
}

export function useWalkthrough(): WalkthroughHookState {
  const {
    fieldActionsPendingSave,
    finishFieldActions,
    finishingFieldActions,
    isFieldWalkthroughFinished,
    isRequesterAlreadyFinished,
    requesterNeedsToClickFinish,
  } = useFinishFieldActions();
  const navigate = useNavigate();
  const dispatch = useNotificationDispatch();
  const currentUserId = useCurrentUserId();
  const { documentId } = useDocumentParams();
  const { walkThroughActive } = useWalkthroughActive();
  const { workflow } = useGetDocumentWorkflow(documentId);
  const {
    requesterWalkthroughInfo: { orderedWalkthroughDocuments },
  } = useReactiveVar(walkthroughStateVar);
  const { navigateToNextAssignedField } = useNavigateToNextAssignedField();
  const { navigateToNextIncompleteCannedField } = useNavigateToNextIncompleteCannedField();
  const { orderedFieldsInCurrentDoc, requesterFieldCounts } = useRequesterOrderedFieldsToFillOut();
  const { cannedFields } = useWorkflowCannedFields(workflow?.id ?? null);
  const incompleteSignerCannedFieldIds = useMemo(
    () =>
      new Set(
        filterCannedFieldsForSigner(cannedFields, currentUserId)
          .filter(f => !getIsCannedFieldComplete(f))
          .map(f => f.nodeId)
      ),
    [cannedFields, currentUserId]
  );

  const advanceWalkthrough = useCallback(
    async (
      e: React.SyntheticEvent,
      options: { canExecuteFinish?: boolean } = {}
    ): Promise<void> => {
      e.preventDefault();

      if (!currentUserId || !walkThroughActive) return;

      const advance = getWalkthroughAdvance({
        canFinish: Boolean(options.canExecuteFinish),
        currentDocumentId: documentId,
        isFieldWalkthroughFinished,
        isInWorkflow: Boolean(workflow),
        orderedFieldsInCurrentDoc,
        orderedWalkthroughDocuments,
        requesterFieldCounts,
      });

      /**
       * These three outcomes are exactly the ones `getWalkthroughAdvance`
       * returns once the signer has finished this doc's regular fields.
       * Canned fields are not part of `orderedFieldsInCurrentDoc`, so that is
       * precisely when a doc can still hold an incomplete canned field — focus
       * it before advancing past the doc. `none` is included because the
       * counts that produce it and the canned set read below come from two
       * different queries, so a still-empty canned field can be known here
       * while the counts already read as complete. The helper no-ops (returns
       * false) when nothing in the current doc matches.
       */
      const isDoneWithCurrentDocFields =
        advance.type === 'navigateToDocument' ||
        advance.type === 'noNextDocument' ||
        advance.type === 'none';
      if (
        isDoneWithCurrentDocFields &&
        navigateToNextIncompleteCannedField(incompleteSignerCannedFieldIds)
      )
        return;

      switch (advance.type) {
        case 'finish': {
          await finishFieldActions();
          return;
        }
        case 'navigateToDocument': {
          if (!workflow) return;
          navigate(
            `${link.DocumentDetailWorkflowDetailFields({ documentId: advance.documentId, workflowId: workflow.id })}?${walkThroughParamKey}=true&${navToNextFieldParamKey}=true`
          );
          return;
        }
        case 'noNextDocument': {
          /**
           * `getWalkthroughAdvance` only weighs regular fields, so it reads
           * "no doc left with work" as a dead end even when a canned field in
           * another doc is still incomplete. Hop to another walkthrough doc
           * and let `navToNextFieldParamKey` focus the canned field there.
           */
          const otherWalkthroughDoc =
            incompleteSignerCannedFieldIds.size > 0
              ? orderedWalkthroughDocuments.find(document => document.id !== documentId)
              : undefined;
          if (workflow && otherWalkthroughDoc) {
            navigate(
              `${link.DocumentDetailWorkflowDetailFields({ documentId: otherWalkthroughDoc.id, workflowId: workflow.id })}?${walkThroughParamKey}=true&${navToNextFieldParamKey}=true`
            );
            return;
          }

          dispatch({
            payload: {
              key: uuid4(),
              message: 'No next document can be found for walkthrough.',
              variant: 'error',
            },
            type: NotificationsActionType.PUSH_NOTIFICATION,
          });
          return;
        }
        case 'focusNextField': {
          navigateToNextAssignedField(orderedFieldsInCurrentDoc);
          return;
        }
        case 'none': {
          return;
        }
      }
    },
    [
      currentUserId,
      dispatch,
      documentId,
      finishFieldActions,
      incompleteSignerCannedFieldIds,
      navigate,
      isFieldWalkthroughFinished,
      navigateToNextAssignedField,
      navigateToNextIncompleteCannedField,
      orderedFieldsInCurrentDoc,
      orderedWalkthroughDocuments,
      requesterFieldCounts,
      walkThroughActive,
      workflow,
    ]
  );

  return {
    advanceWalkthrough,
    fieldActionsPendingSave,
    isFieldWalkthroughFinished,
    isRequesterAlreadyFinished,
    requesterFieldCounts,
    requesterNeedsToClickFinish,
    submitting: finishingFieldActions,
    walkThroughActive,
  };
}

interface RequesterVariable extends Pick<
  Apollo.DocumentVariable,
  | 'deletedAt'
  | 'id'
  | 'label'
  | 'isField'
  | 'nodeId'
  | '__typename'
  | 'value'
  | 'variant'
  | 'minRequired'
  | 'maxRequired'
  | 'skipped'
> {
  action: ExistingFieldRecipient | null;
  isComplete: boolean;
  selectedAnswer: Pick<Apollo.AnswerOption, 'id' | '__typename'> | null;
  selectedAnswers: AnswerOptionSubset[] | null;
}
interface RequesterSignature extends Pick<
  Apollo.SignatureField,
  'id' | 'allSigned' | 'nodeId' | '__typename'
> {
  action: ExistingFieldRecipient | null;
  isComplete: boolean;
}
type RequesterField = RequesterVariable | RequesterSignature;

export function useRequesterOrderedFieldsToFillOut(): {
  orderedFieldsInCurrentDoc: RequesterField[];
  requesterFieldCounts: RequesterFieldCounts;
} {
  const currentUserId = useCurrentUserId();
  const { documentId } = useDocumentParams();
  const {
    requesterWalkthroughInfo: { walkthroughDocumentsById },
  } = useReactiveVar(walkthroughStateVar);
  const { tabId } = useRequiredEditorRuntime();
  const { document } = useGetCurrentDoc(documentId);
  const { cannedFields } = useWorkflowCannedFields(document?.workflow?.id ?? null);
  const signerCannedFields = filterCannedFieldsForSigner(cannedFields, currentUserId);
  const { requesterFields = [] } = walkthroughDocumentsById[documentId] ?? {};
  const { sortFieldsByDocPosition } = useGetCompareFieldsByDocPosition(tabId);
  const orderedFieldsInCurrentDoc = useMemo(() => {
    return [...requesterFields].sort(sortFieldsByDocPosition);
  }, [requesterFields, sortFieldsByDocPosition]);
  const allRequesterFields = Object.values(walkthroughDocumentsById).flatMap(
    ({ requesterFields }) => requesterFields
  );
  const cannedCompleted = signerCannedFields.filter(getIsCannedFieldComplete).length;

  return {
    orderedFieldsInCurrentDoc,
    requesterFieldCounts: {
      completed: allRequesterFields.filter(({ isComplete }) => isComplete).length + cannedCompleted,
      total: allRequesterFields.length + signerCannedFields.length,
    },
  };
}

function getIsCannedFieldComplete(
  cannedField: Pick<Apollo.CannedFieldFullFragment, 'defaultValue' | 'value'>
): boolean {
  const resolvedValue =
    typeof cannedField.value === 'string' ? cannedField.value : cannedField.defaultValue;
  return Boolean(resolvedValue?.trim());
}

function filterCannedFieldsForSigner(
  cannedFields: readonly Apollo.CannedFieldFullFragment[],
  currentUserId: UUID | null
): Apollo.CannedFieldFullFragment[] {
  if (!currentUserId) return [];
  return cannedFields.filter(
    field => field.placements.length > 0 && field.assignee?.assignee?.id === currentUserId
  );
}

export function useNavigateToNextIncompleteCannedField(): {
  navigateToNextIncompleteCannedField: (incompleteCannedIds: ReadonlySet<UUID>) => boolean;
} {
  const editor = useMaybeEditor();
  const tabId = useRequiredEditorRuntime().tabId;
  const documentViewer = useMaybeDocumentViewer();
  const rWHeightPx = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.reactWindow.windowHeight
  );
  const rWOffsetPx = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.reactWindow.scrollOffset
  );

  const focusPanelRow = useCallback(
    (cannedFieldId: UUID) => {
      setVariablePanelFocusedField(tabId, {
        __typename: 'CannedField',
        id: cannedFieldId,
        nodeId: cannedFieldId,
      });
    },
    [tabId]
  );

  const navigateToNextIncompleteCannedField = useCallback(
    (incompleteCannedIds: ReadonlySet<UUID>): boolean => {
      if (incompleteCannedIds.size === 0) return false;

      /**
       * "Current" is the caret's canned field in the editor, falling back to
       * the focused panel row — the only signal a PDF has, and what the
       * previous press set. Cycling from it (with wrap-around) lets Next walk
       * every incomplete canned field the way it walks regular fields,
       * instead of re-selecting the first one forever.
       */
      const focusedField = getVariablePanelFocusedField(tabId);
      const focusedCannedFieldId =
        focusedField?.__typename === 'CannedField' ? focusedField.nodeId : null;

      if (editor) {
        const currentEntry = Editor.above(editor, { match: isCannedFieldElement });
        const currentCannedFieldId = currentEntry?.[0].cannedFieldId ?? focusedCannedFieldId;

        const orderedIncompleteIds: UUID[] = [];
        for (const [{ cannedFieldId }] of Queries.cannedFieldEntries(editor)) {
          if (!incompleteCannedIds.has(cannedFieldId)) continue;
          if (!orderedIncompleteIds.includes(cannedFieldId))
            orderedIncompleteIds.push(cannedFieldId);
        }

        if (orderedIncompleteIds.length) {
          const currentIndex = currentCannedFieldId
            ? orderedIncompleteIds.indexOf(currentCannedFieldId)
            : -1;
          const nextId = orderedIncompleteIds[(currentIndex + 1) % orderedIncompleteIds.length];
          if (!nextId) return false;

          if (nextId === currentCannedFieldId) {
            /**
             * This is the only incomplete canned field and the signer is
             * already on it. Move to another placement of it if one exists;
             * otherwise claim the press and do nothing. Re-scrolling in place
             * reads as a dead button, and returning false would fall through
             * to the "no next document" error while they still have this
             * field to fill.
             */
            const nextEntry = Queries.getNextCannedFieldElement(editor, {
              cannedFieldId: nextId,
            });
            if (nextEntry && nextEntry[0] !== currentEntry?.[0]) {
              ClientTransforms.scrollToLocation(editor, {
                affinity: 'last',
                cMLocation: Editor.start(editor, nextEntry[1]),
                rWHeightPx,
                rWOffsetPx,
              });
            }
            focusPanelRow(nextId);
            return true;
          }

          const entry = Queries.cannedFieldEntries(editor).find(
            ([el]) => el.cannedFieldId === nextId
          );
          if (entry) {
            ClientTransforms.scrollToLocation(editor, {
              affinity: 'last',
              cMLocation: Editor.start(editor, entry[1]),
              rWHeightPx,
              rWOffsetPx,
            });
            focusPanelRow(nextId);
            return true;
          }
        }
      }

      if (documentViewer) {
        const annotationManager = documentViewer.getAnnotationManager();
        // First widget of each incomplete canned field, in reading order —
        // the raw annotation list carries no document order of its own.
        const sortedAnnots = [...annotationManager.getAnnotationsList()].sort(
          (a, b) => a.PageNumber - b.PageNumber || a.Y - b.Y || a.X - b.X
        );
        const widgetByCannedId = new Map<UUID, (typeof sortedAnnots)[number]>();
        for (const annot of sortedAnnots) {
          const cannedFieldId = annot.getCustomData(PDFCustomDataKey.cannedFieldId);
          if (!cannedFieldId || !incompleteCannedIds.has(cannedFieldId)) continue;
          if (!widgetByCannedId.has(cannedFieldId)) widgetByCannedId.set(cannedFieldId, annot);
        }

        const orderedIncompleteIds = [...widgetByCannedId.keys()];
        if (orderedIncompleteIds.length) {
          const currentIndex = focusedCannedFieldId
            ? orderedIncompleteIds.indexOf(focusedCannedFieldId)
            : -1;
          const nextId = orderedIncompleteIds[(currentIndex + 1) % orderedIncompleteIds.length];
          const target = nextId ? widgetByCannedId.get(nextId) : undefined;
          if (nextId && target) {
            documentViewer.displayPageLocation(target.getPageNumber(), target.X, target.Y, true);
            focusPanelRow(nextId);
            return true;
          }
        }
      }

      return false;
    },
    [documentViewer, editor, focusPanelRow, rWHeightPx, rWOffsetPx, tabId]
  );

  return { navigateToNextIncompleteCannedField };
}

export function useNavigateToNextAssignedField(): {
  navigateToNextAssignedField: (orderedFieldsInCurrentDoc: RequesterField[]) => void;
} {
  const { tabId } = useRequiredEditorRuntime();
  const { scrollFieldIntoView } = useScrollFieldIntoView();
  const { getNextAssignedField } = useGetNextAssignedField();
  const navigateToNextAssignedField = useCallback(
    (orderedFieldsInCurrentDoc: RequesterField[]) => {
      const nextField = getNextAssignedField(orderedFieldsInCurrentDoc);
      if (nextField) {
        setVariablePanelFocusedField(tabId, nextField);
        scrollFieldIntoView(nextField);
      }
    },
    [getNextAssignedField, scrollFieldIntoView, tabId]
  );

  return { navigateToNextAssignedField };
}

export function useGetNextAssignedField(): {
  getNextAssignedField: (orderedFieldsInCurrentDoc: RequesterField[]) => RequesterField | null;
} {
  const { tabId } = useRequiredEditorRuntime();
  const focusedField = useVariablePanelFocusedField(tabId);

  const getNextAssignedField = useCallback(
    (orderedFieldsInCurrentDoc: RequesterField[]): RequesterField | null => {
      const isUserFinishedWithCurrentDoc = orderedFieldsInCurrentDoc.every(
        ({ isComplete }) => isComplete
      );
      const fieldIndex = orderedFieldsInCurrentDoc.findIndex(
        field => field.nodeId === focusedField?.nodeId
      );
      const fieldsAfterIndex = orderedFieldsInCurrentDoc.slice(fieldIndex + 1);
      const noIncompleteFieldsAfterIndex = fieldsAfterIndex.every(({ isComplete }) => isComplete);
      const nextField = orderedFieldsInCurrentDoc.find((field, index) => {
        const isLastIndex = fieldIndex === orderedFieldsInCurrentDoc.length - 1;
        if (isUserFinishedWithCurrentDoc)
          return isLastIndex ? index === 0 : index === fieldIndex + 1;

        if (noIncompleteFieldsAfterIndex) return !field.isComplete;

        return index > fieldIndex && !field.isComplete;
      });

      return nextField ?? null;
    },
    [focusedField?.nodeId]
  );

  return { getNextAssignedField };
}

interface DocWithRequesterWalkthroughFields extends Pick<
  Apollo.Document,
  'createdAt' | 'id' | 'isSavePending' | '__typename'
> {
  orderId: string;
  requesterFields: RequesterField[];
}
type WalkthroughDocumentsById = Record<UUID, DocWithRequesterWalkthroughFields>;

interface RequesterWalkthroughInfo {
  hasPendingDocumentSave: boolean;
  orderedWalkthroughDocuments: DocWithRequesterWalkthroughFields[];
  ready: boolean;
  requesterActionsToDo: Pick<Apollo.WorkflowAction, 'completedAt' | 'id' | '__typename'>[];
  walkthroughDocumentsById: WalkthroughDocumentsById;
}

export function useSetRequesterWalkthroughInfo(): void {
  const currentUserId = useCurrentUserId();
  const { tabId } = useRequiredEditorRuntime();
  const { documentId } = useDocumentParams();
  const { fieldsChanged } = useFieldsChanged(tabId);
  const { isInSigningCeremony: editorIsInSigningCeremony } = useGetEditorUserState();
  const { allSignatures, allVariables, document } = useGetDocumentFields(documentId);
  const { loading: loadingWorkflow, workflow } = useGetDocumentWorkflow(documentId);
  const {
    loading: loadingWalkthroughWorkflow,
    refetch: refetchWalkthroughWorkflow,
    workflow: walkthroughWorkflow,
  } = useGetWalkthroughWorkflow(workflow?.id ?? null);
  /**
   * Apollo 4 flips `loading` back to true for the duration of every refetch,
   * and the walkthrough workflow is refetched whenever a field changes. Gating
   * only on `loading` would therefore blank the whole walkthrough between
   * refetches, and a Next tap landing in that window sees zero fields to do.
   * A query that still has data to read is not "checking".
   */
  const checkingWorkflow =
    (loadingWorkflow && !workflow) || (loadingWalkthroughWorkflow && !walkthroughWorkflow);
  const requesterWalkthroughInfo = useMemo((): RequesterWalkthroughInfo => {
    // Don't return any walkthrough info before we've checked if the Document is part of a Workflow.
    if (checkingWorkflow) return defaultWalkthroughState.requesterWalkthroughInfo;

    if (!walkthroughWorkflow) {
      const isInSigningCeremony = editorIsInSigningCeremony;
      const walkthroughDocumentsById = document
        ? [...allVariables, ...allSignatures].reduce<WalkthroughDocumentsById>((acc, field) => {
            const { requesterFields = [] } = acc[document.id] ?? {};
            const isFieldDeleted = Boolean(field.deletedAt);
            const isAssignedToRequester = field.action?.assignee?.id === currentUserId;
            if (!isAssignedToRequester || isFieldDeleted) return acc;

            const maybeRequesterField = createRequesterFieldToDo(field, {
              isInSigningCeremony,
            });
            if (!maybeRequesterField) return acc;

            acc[document.id] = {
              __typename: 'Document',
              createdAt: document.createdAt,
              id: document.id,
              isSavePending: document.isSavePending,
              orderId: '1',
              requesterFields: [...requesterFields, maybeRequesterField],
            };
            return acc;
          }, {})
        : {};

      return {
        hasPendingDocumentSave: Boolean(document?.isSavePending),
        orderedWalkthroughDocuments:
          Object.values(walkthroughDocumentsById).sort(defaultWorkflowFileSort),
        ready: true,
        requesterActionsToDo: [],
        walkthroughDocumentsById,
      };
    }

    const workflowSentForSig = getWorkflowWasSentForSig(walkthroughWorkflow);
    // Derive isInSigningCeremony from the walkthrough workflow (more authoritative than the
    // editor's cache-first query, which may be stale after a state transition).
    const isInSigningCeremony = workflowSentForSig || editorIsInSigningCeremony;
    // No fields for anyone to do b/c workflow is not yet sent out...
    const requesterWalkthroughFieldsToDo = workflowSentForSig
      ? (walkthroughWorkflow.activeStep?.requesterActions.flatMap(a => a.fields) ?? [])
      : [];
    const walkthroughDocumentsById =
      requesterWalkthroughFieldsToDo.reduce<WalkthroughDocumentsById>((acc, field) => {
        const isFieldDeleted = Boolean(field.deletedAt);
        const isAssignedToRequester = field.action?.assignee?.id === currentUserId;
        if (!isAssignedToRequester || isFieldDeleted) return acc;

        const { requesterFields = [] } = acc[field.document.id] ?? {};
        const maybeRequesterField = createRequesterFieldToDo(field, {
          isInSigningCeremony,
        });
        const file = walkthroughWorkflow.files.find(f => f.document.id === field.document.id);
        if (!maybeRequesterField || !file) return acc;

        acc[field.document.id] = {
          __typename: 'Document',
          createdAt: field.document.createdAt,
          id: field.document.id,
          isSavePending: file.document.isSavePending,
          orderId: file.orderId,
          requesterFields: [...requesterFields, maybeRequesterField],
        } satisfies DocWithRequesterWalkthroughFields;

        return acc;
      }, {});

    return {
      hasPendingDocumentSave: walkthroughWorkflow.files.some(file => file.document.isSavePending),
      orderedWalkthroughDocuments:
        Object.values(walkthroughDocumentsById).sort(defaultWorkflowFileSort),
      ready: true,
      requesterActionsToDo: workflowSentForSig
        ? (walkthroughWorkflow.activeStep?.requesterActions ?? [])
        : [],
      walkthroughDocumentsById,
    };
  }, [
    allSignatures,
    allVariables,
    checkingWorkflow,
    currentUserId,
    document,
    editorIsInSigningCeremony,
    walkthroughWorkflow,
  ]);

  useEffect(() => {
    setWalkthroughState({ requesterWalkthroughInfo });
  }, [requesterWalkthroughInfo]);

  useAsync(async () => {
    if (workflow?.id && fieldsChanged)
      await refetchWalkthroughWorkflow({ workflowId: workflow.id });
  }, [workflow?.id, fieldsChanged]);
}

export function createRequesterFieldToDo(
  field: Omit<RequesterVariable, 'isComplete'> | Omit<RequesterSignature, 'isComplete'>,
  options: {
    isInSigningCeremony: boolean;
  }
): RequesterField | null {
  const { isInSigningCeremony } = options;
  switch (field.__typename) {
    case 'DocumentVariable': {
      return {
        ...field,
        isComplete: getIsFieldComplete(field),
      };
    }
    case 'SignatureField':
    default: {
      if (!isInSigningCeremony) return null;

      return {
        ...field,
        isComplete: getIsFieldComplete(field),
      };
    }
  }
}

export function useNavToNextFieldFromURL() {
  const currentUserId = useCurrentUserId();
  const { document } = useGetCurrentDoc();
  const editorLoading = useEditorLoading();
  const { tabId } = useRequiredEditorRuntime();
  const annotationsLoaded = useAreAnnotationsLoaded();
  const handledNavigationRef = useRef<string | null>(null);
  const { scrollFieldIntoView } = useScrollFieldIntoView();
  const { getNextAssignedField } = useGetNextAssignedField();
  const { navigateToNextIncompleteCannedField } = useNavigateToNextIncompleteCannedField();
  const { cannedFields } = useWorkflowCannedFields(document?.workflow?.id ?? null);
  const searchParams = new URLSearchParams(window.location.search);
  const navigationRequestKey = getNavToNextFieldRequestKey({
    documentId: document?.id,
    paramValue: searchParams.get(navToNextFieldParamKey),
  });
  const { orderedFieldsInCurrentDoc } = useRequesterOrderedFieldsToFillOut();
  const isLoading =
    document?.latestVersion.__typename === 'DocumentVersion' ? editorLoading : !annotationsLoaded;
  const incompleteCannedIds = useMemo(
    () =>
      new Set(
        filterCannedFieldsForSigner(cannedFields, currentUserId)
          .filter(f => !getIsCannedFieldComplete(f))
          .map(f => f.nodeId)
      ),
    [cannedFields, currentUserId]
  );

  useEffect(() => {
    if (!navigationRequestKey) {
      handledNavigationRef.current = null;
      return;
    }
    if (isLoading || !document || handledNavigationRef.current === navigationRequestKey) return;
    handledNavigationRef.current = navigationRequestKey;

    const nextField = getNextAssignedField(orderedFieldsInCurrentDoc);
    if (!nextField) {
      navigateToNextIncompleteCannedField(incompleteCannedIds);
      return deleteNavToNextFieldParam();
    }

    setVariablePanelFocusedField(tabId, nextField);

    switch (nextField.__typename) {
      case 'DocumentVariable': {
        scrollFieldIntoView(nextField);
        break;
      }
      case 'SignatureField': {
        scrollFieldIntoView(nextField);
        break;
      }
    }

    deleteNavToNextFieldParam();
  }, [
    document,
    getNextAssignedField,
    incompleteCannedIds,
    isLoading,
    navigateToNextIncompleteCannedField,
    navigationRequestKey,
    orderedFieldsInCurrentDoc,
    scrollFieldIntoView,
    tabId,
  ]);
}

/**
 * Identity of one "jump to the next field" request, used only to tell whether
 * this hook already handled it. The document is part of it because the same
 * `?navToNextField=true` carries over when the walkthrough advances to another
 * document, and that is a new request the hook has to act on again.
 */
function getNavToNextFieldRequestKey(args: {
  documentId: UUID | undefined;
  paramValue: string | null;
}): string | null {
  const { documentId, paramValue } = args;
  if (!documentId || !paramValue) return null;
  return `${documentId}:${paramValue}`;
}

function deleteNavToNextFieldParam(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(navToNextFieldParamKey);
  updateUrlWithoutRefresh(url.toString());
}

export function useGetStartWalkthrough(): {
  startWalkthrough: () => void;
} {
  const editor = useEditor();
  const navigate = useNavigate();
  const {
    documentUi: { setEditingMode },
  } = useEditorTabStateActions();
  const { document } = useGetCurrentDoc();
  const { documentId } = useDocumentParams();
  const { openDrawer } = useGlobalDrawer();
  const documentViewer = useMaybeDocumentViewer();
  const startWalkthrough = useCallback(() => {
    const incoming = new URLSearchParams(window.location.search);
    const params = new URLSearchParams({ [walkThroughParamKey]: 'true' });
    if (incoming.get(declineParamKey) === 'true') params.set(declineParamKey, 'true');
    navigate(`${link.DocumentDetailFields({ documentId })}?${params.toString()}`, {
      replace: true,
    });
    openDrawer();
    setEditingMode(EditingMode.VIEWING);

    switch (document?.latestVersion.__typename) {
      case 'DocumentVersion': {
        if (
          CollaborationEditor.isCollaborationEditor(editor) &&
          CollaborationEditor.connected(editor)
        ) {
          AlineTransforms.select(editor, Editor.start(editor, []), {
            eventSource: SelEventSource.PROGRAMMATIC,
          });
          /**
           * On mobile the walkthrough auto-starts from a URL param inside a
           * mount effect (no user gesture). Focusing the hidden contenteditable
           * there surfaces the Android soft keyboard immediately; the
           * MobileSigningFieldSheet provides its own inputs, so the editor
           * never needs DOM focus on mobile. The Slate selection above is still
           * set so the walkthrough has a valid anchor.
           */
          if (!IS_MOBILE) ClientTransforms.focus(editor);
        }
        break;
      }
      case 'StaticFileVersion': {
        documentViewer?.displayFirstPage();
        break;
      }
    }
  }, [
    document?.latestVersion.__typename,
    documentId,
    documentViewer,
    editor,
    navigate,
    openDrawer,
    setEditingMode,
  ]);

  return { startWalkthrough };
}

export function useRequesterHasWalkthroughActionsToComplete(): {
  requesterHasWalkthroughActionsToComplete: boolean;
} {
  const { document } = useGetCurrentDoc();
  const {
    requesterWalkthroughInfo: { orderedWalkthroughDocuments, requesterActionsToDo },
  } = useReactiveVar(walkthroughStateVar);
  const userHasActionsToComplete = requesterActionsToDo.some(a => !a.completedAt);
  const userHasFieldsToComplete = orderedWalkthroughDocuments.some(({ requesterFields }) =>
    requesterFields.some(({ isComplete }) => !isComplete)
  );
  const userHasIncompleteActions = userHasActionsToComplete || userHasFieldsToComplete;
  const requesterHasWalkthroughActionsToComplete =
    document?.variant === Apollo.DocumentVariant.Document && userHasIncompleteActions;

  return { requesterHasWalkthroughActionsToComplete };
}

export function useGetWalkthroughWorkflow(workflowId: UUID | null) {
  const { data, loading, refetch } = Apollo.useGetWalkthroughWorkflowQuery({
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
  });
  // Wrap in useMemo so downstream hooks don't loop infinitely...
  const { requesterActionsInActiveStep, workflow } = useMemo(() => {
    return {
      requesterActionsInActiveStep: data?.workflow?.activeStep?.requesterActions ?? [],
      workflow: data?.workflow ?? null,
    };
  }, [data?.workflow]);

  return {
    loading,
    refetch,
    requesterActionsInActiveStep,
    workflow,
  };
}

export const finishingFieldActionsVar = makeVar<boolean>(false);

export function useFinishFieldActions() {
  const navigate = useNavigate();
  const dispatch = useNotificationDispatch();
  const currentUserId = useCurrentUserId();
  const { documentId } = useDocumentParams();
  const tabId = useCurrentTabIdOrThrow();
  const syncingDoc = useEditorTabStateSelectorForTabId(tabId, state => state.viewer.syncingDoc);
  const pendingCannedFieldValueIdsByWorkflowId = useReactiveVar(
    pendingCannedFieldValueIdsByWorkflowIdVar
  );
  const {
    requesterWalkthroughInfo: {
      hasPendingDocumentSave,
      orderedWalkthroughDocuments,
      ready: requesterWalkthroughReady,
      requesterActionsToDo,
    },
  } = useReactiveVar(walkthroughStateVar);
  const { workflow } = useGetDocumentWorkflow(documentId);
  const {
    cannedFields,
    hasError: cannedFieldsHaveError,
    loading: cannedFieldsLoading,
  } = useWorkflowCannedFields(workflow?.id ?? null);
  const finishingFieldActions = useReactiveVar(finishingFieldActionsVar);
  const hasPendingCannedValueWrite = Boolean(
    workflow?.id && (pendingCannedFieldValueIdsByWorkflowId[workflow.id]?.size ?? 0) > 0
  );
  const hasPendingSave =
    syncingDoc === true || hasPendingDocumentSave || hasPendingCannedValueWrite;
  const requesterCompletedAllActions = requesterActionsToDo.every(a => Boolean(a.completedAt));
  const requesterCompletedAllFields =
    requesterWalkthroughReady &&
    Boolean(workflow) &&
    orderedWalkthroughDocuments.every(({ requesterFields }) =>
      requesterFields.every(({ isComplete }) => isComplete)
    );
  const placedCannedFields = cannedFields.filter(field => field.placements.length > 0);
  const unassignedCannedFields = placedCannedFields.filter(field => !field.assignee);
  const incompleteSignerCannedFields = filterCannedFieldsForSigner(
    placedCannedFields,
    currentUserId
  ).filter(field => !getIsCannedFieldComplete(field));
  const cannedFieldsReady =
    !cannedFieldsLoading &&
    !cannedFieldsHaveError &&
    unassignedCannedFields.length === 0 &&
    incompleteSignerCannedFields.length === 0;
  const requesterCompletedRequiredFields = requesterCompletedAllFields && cannedFieldsReady;
  const isFieldWalkthroughFinished = !hasPendingSave && requesterCompletedRequiredFields;
  const [finishFieldActions] = Apollo.useFinishFieldActionsMutation();
  const isRequesterAlreadyFinished =
    requesterCompletedAllActions && requesterCompletedRequiredFields;
  const requesterNeedsToClickFinish =
    requesterCompletedRequiredFields && !requesterCompletedAllActions;

  /**
   * No effect cleanup: React.Activity would drop the listener on tab hide.
   * Keyed by workflow.id so a different pane cannot overwrite this one's
   * entry. Cleared explicitly after a successful finish so a stale entry does
   * not follow the signer onto the done page.
   */
  useFinishSigningUnloadGuard(workflow?.id, requesterNeedsToClickFinish);

  return {
    fieldActionsPendingSave: hasPendingSave,
    finishFieldActions: useCallback(async () => {
      if (!isFieldWalkthroughFinished) {
        dispatch({
          payload: {
            key: uuid4(),
            message: getFinishBlockingMessage({
              cannedFieldsHaveError,
              cannedFieldsLoading,
              hasPendingSave,
              incompleteSignerCannedFields,
              requesterCompletedAllFields,
              unassignedCannedFields,
            }),
            variant: 'error',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }

      if (!workflow?.id) return;

      if (isRequesterAlreadyFinished) {
        navigate(link.WorkflowDoneSigning({ workflowId: workflow.id }));
        return;
      }

      finishingFieldActionsVar(true);
      try {
        const { data } = await finishFieldActions({
          // Refetch workflow files since this may be the last signer...
          refetchQueries: [
            {
              query: Apollo.GetWorkflowFilesDocument,
              variables: {
                workflowId: workflow.id,
              } satisfies Apollo.GetWorkflowFilesQueryVariables,
            },
          ],
          variables: { workflowId: workflow.id },
        });
        if (data) {
          setFinishSigningUnloadGuardForWorkflow(workflow.id, false);
          navigate(link.WorkflowDoneSigning({ workflowId: workflow.id }));
        }
      } catch (error) {
        dispatch({
          payload: {
            key: uuid4(),
            message: getFinishFailureMessage(error),
            variant: 'error',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      } finally {
        finishingFieldActionsVar(false);
      }
    }, [
      cannedFieldsHaveError,
      cannedFieldsLoading,
      dispatch,
      finishFieldActions,
      hasPendingSave,
      incompleteSignerCannedFields,
      isFieldWalkthroughFinished,
      isRequesterAlreadyFinished,
      navigate,
      requesterCompletedAllFields,
      unassignedCannedFields,
      workflow?.id,
    ]),
    finishingFieldActions,
    isFieldWalkthroughFinished,
    isRequesterAlreadyFinished,
    requesterNeedsToClickFinish,
  };
}

interface FinishBlockingMessageOptions {
  readonly cannedFieldsHaveError: boolean;
  readonly cannedFieldsLoading: boolean;
  readonly hasPendingSave: boolean;
  readonly incompleteSignerCannedFields: readonly Apollo.CannedFieldFullFragment[];
  readonly requesterCompletedAllFields: boolean;
  readonly unassignedCannedFields: readonly Apollo.CannedFieldFullFragment[];
}

function getFinishBlockingMessage(options: FinishBlockingMessageOptions): string {
  if (options.hasPendingSave) return 'Wait for changes to finish saving before signing.';
  if (options.cannedFieldsLoading)
    return 'Wait for canned fields to finish loading before signing.';
  if (options.cannedFieldsHaveError)
    return 'Canned fields could not be loaded. Reload the document before signing.';

  const unassignedLabels = getCannedFieldLabels(options.unassignedCannedFields);
  if (unassignedLabels.length)
    return `Assign these canned fields before signing: ${unassignedLabels.join(', ')}.`;

  const incompleteLabels = getCannedFieldLabels(options.incompleteSignerCannedFields);
  if (incompleteLabels.length)
    return `Complete these canned fields before signing: ${incompleteLabels.join(', ')}.`;

  if (!options.requesterCompletedAllFields) return 'Must complete all fields to finish.';

  return 'Signing requirements have not finished loading. Try again.';
}

function getCannedFieldLabels(cannedFields: readonly Apollo.CannedFieldFullFragment[]): string[] {
  return [...new Set(cannedFields.map(field => CANNED_FIELD_METADATA[field.variant].label))];
}

function getFinishFailureMessage(error: unknown): string {
  if (CombinedGraphQLErrors.is(error))
    return error.errors.at(0)?.message ?? 'Could not finish signing.';
  if (error instanceof Error) return error.message;
  return 'Could not finish signing.';
}
